import {
  Component,
  EventEmitter,
  inject,
  Output,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { MegaMenuItem, MenuItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { EInviteStatus, IInvite } from 'src/app/common/model/invites.model';
import {
  ICurrentOrganization,
  IOrganization
} from 'src/app/common/model/organization.model';
import { IUser } from 'src/app/common/model/user.model';
import { FileService } from 'src/app/common/services/file/file.service';
import { InvitesService } from 'src/app/common/services/invites/invites.service';
import { OrganizationService } from 'src/app/common/services/organization/organization.service';
import { UserService } from 'src/app/common/services/user/user.service';
import { UtilService } from 'src/app/common/services/util/util.service';
import { LayoutService } from '../../service/app.layout.service';

@Component({
  selector: 'app-profilemenu',
  templateUrl: './app.profilesidebar.component.html'
})
export class AppProfileSidebarComponent implements OnDestroy, OnInit {
  readonly router = inject(Router);
  readonly layoutService = inject(LayoutService);
  readonly userService = inject(UserService);
  readonly inviteService = inject(InvitesService);
  readonly organizationService = inject(OrganizationService);
  readonly utilService = inject(UtilService);
  readonly fileService = inject(FileService);

  date: Date = new Date();
  profilePicture: any;
  subscriptions = new Subscription();
  user!: IUser | undefined;
  invites: IInvite[] = [];
  organizationsMenuItems: MegaMenuItem[] = [];
  organizations: IOrganization[] = [];
  currentOrganization!: ICurrentOrganization;

  get visible(): boolean {
    return this.layoutService.state.rightMenuActive;
  }

  set visible(_val: boolean) {
    this.layoutService.state.rightMenuActive = _val;
  }

  @Output() phoneNumberAvailable = new EventEmitter<boolean>();

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.getUserDetails();
    this.getAllOrganizations();
    this.getReferralCode();
  }

  getProfileImage(fileId: string): void {
    this.subscriptions.add(
      this.fileService.downloadImageFile(fileId).subscribe({
        next: (res) => {
          this.createImageFromBlob(res);
        }
      })
    );
  }

  createImageFromBlob(image: Blob) {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        this.profilePicture = reader.result;
      },
      false
    );

    if (image) {
      reader.readAsDataURL(image);
    }
  }

  getUserDetails(): void {
    this.subscriptions.add(
      this.userService.getUserProfile().subscribe({
        next: (res) => {
          this.user = res.result;
        },
        complete: () => {
          this.getAllInvites();
          if (this.user?.phone) {
            this.phoneNumberAvailable.emit(true);
          }
        }
      })
    );
  }

  getAllInvites(): void {
    this.subscriptions.add(
      this.inviteService.getAllInvites().subscribe((response) => {
        this.invites = response.result;
      })
    );
  }

  acceptInvite(invite: IInvite) {
    const inviteArr = {
      inviteId: invite._id,
      status: EInviteStatus.ACCEPTED
    };
    this.inviteService.acceptInvite(inviteArr).subscribe((response) => {
      // this.errorMessage = `Your are now  a member of ${invite.organization.name}`;
      setTimeout(() => {
        this.getAllInvites();
      }, 4000);
    });
  }

  rejectInvite(invite: IInvite) {
    const inviteArr = {
      inviteId: invite._id,
      status: EInviteStatus.REJECTED
    };
    this.inviteService.rejectInvite(inviteArr).subscribe((response) => {
      //   this.errorMessage = `Invite to ${invite.organization.name} rejected!`;
      setTimeout(() => {
        this.getAllInvites();
      }, 4000);
    });
  }

  getAllOrganizations() {
    this.subscriptions.add(
      this.organizationService.getAllOrganizations().subscribe({
        next: (res) => {
          this.organizations = res.result;
          if (this.organizations?.length > 0) {
            this.getCurrentOrganization();
          }
        }
      })
    );
  }

  getCurrentOrganization(): void {
    this.subscriptions.add(
      this.organizationService.getCurrentOrganization().subscribe({
        next: (res) => {
          this.currentOrganization = res.result;
          const organizationCurrency = res?.result?.organization?.currency?.symbol
            ? res?.result?.organization?.currency?.symbol
            : res?.result?.organization?.currency?.code;
          this.utilService.setCurrentCurrency(organizationCurrency);
        },
        complete: () => this.getMenuItems()
      })
    );
  }

  getMenuItems() {
    this.organizationsMenuItems = [
      {
        label: this.currentOrganization.organization.name,
        icon: 'pi pi-building',
        items: [
          [
            {
              label: 'Switch organization',
              escape: false,
              items: this.organizations
                .map((x) => {
                  return {
                    label: x.name,
                    icon:
                      x._id === this.currentOrganization.organization._id
                        ? 'pi pi-check-circle text-green-500'
                        : null,
                    command: () => {
                      this.switchToOrganization(x._id);
                    }
                  };
                })
                .slice(0, 5) as MenuItem[]
            },
            {
              label: '',
              items: [
                {
                  label: 'Manage organizations',
                  icon: 'pi pi-building',
                  routerLink: '/organization/select'
                }
              ]
            }
          ]
        ]
      }
    ];
  }

  switchToOrganization(organizationId: string | undefined) {
    const currency = localStorage.getItem('currency');
    if (currency) {
      localStorage.removeItem('currency');
    }
    this.subscriptions.add(
      this.organizationService
        .setCurrentOrganization(organizationId as string)
        .subscribe((res) => {
          this.router.navigate([`/`]).then(() => {
            window.location.reload();
          });
        })
    );
  }

  getReferralCode(): void {
    const referralCode = localStorage.getItem('referralCode');
    if (referralCode != null) {
      this.subscriptions.add(
        this.userService.addUserReferralCode(referralCode).subscribe((res) => {
          localStorage.removeItem('referralCode');
        })
      );
    }
  }
}
